import React, { useCallback, useEffect, useState, useRef } from "react";
import Parse from "parse";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
Parse.initialize(
  process.env.REACT_APP_APP_ID,
  null,
  process.env.REACT_APP_MASTER_KEY
);
Parse.masterKey = process.env.REACT_APP_MASTER_KEY;
Parse.serverURL = process.env.REACT_APP_SERVER_URL;

const CLKKWidget = ({
  open,
  onClose,
  userId,
  walletId,
  remark,
  platform,
  type,
  amount,
  sessionToken,
}) => {
  const navigate = useNavigate();
  const iframeRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [error, setError] = useState(null);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [checkoutResponse, setCheckoutResponse] = useState(null);

  const allowedOrigins = [
    "https://pay.clkkapi.io",
    "https://pay-dev.clkkapi.io",
  ];

  // ✅ Create Checkout Session once widget opens
  useEffect(() => {
    if (!open || !userId || !amount) return;

    const createSession = async () => {
      try {
        // Call your cloud function
        const response = await Parse.Cloud.run(
          "createCheckoutSession",
          {
            amount,
            userId,
          },
          { sessionToken: sessionToken }
        );

        // Save transaction record
        const TransactionRecords = Parse.Object.extend("TransactionRecords");
        const transaction = new TransactionRecords();

        transaction.set("type", type);
        transaction.set("username", ""); // optional: set if you have username
        transaction.set("userId", userId);
        transaction.set("transactionDate", new Date());
        transaction.set("transactionAmount", amount);
        transaction.set("remark", remark);
        transaction.set("useWallet", false);
        transaction.set("userParentId", ""); // optional: set if you track parent agent
        transaction.set("status", 1); // pending
        transaction.set("portal", "CLK");
        transaction.set("referralLink", response?.publicUrl);
        transaction.set("transactionIdFromStripe", response?.id);
        transaction.set("walletAddr", walletId);

        await transaction.save(null, { useMasterKey: true });

        setCheckoutResponse(response);
        setLoading(true);
      } catch (err) {
        console.error("❌ Failed to create session:", err);
        setError("Could not create payment session.");
        setLoading(false);
      }
    };

    createSession();
  }, [open, userId, amount, remark, type, walletId]);

  // ✅ Update transaction status
  const updateTransactionStatus = async (sessionId, status) => {
    try {
      const TransactionRecords = Parse.Object.extend("TransactionRecords");
      const query = new Parse.Query(TransactionRecords);
      query.equalTo("transactionIdFromStripe", sessionId);
      const txn = await query.first({ useMasterKey: true });

      if (!txn) {
        console.warn("⚠️ No transaction found for session:", sessionId);
        return;
      }

      txn.set("status", status);
      await txn.save(null, { useMasterKey: true });

      if ([2, 3].includes(status)) {
        const amount = txn.get("transactionAmount");
        const userId = txn.get("userId");

        if (amount && userId) {
          const userQuery = new Parse.Query(Parse.User);
          userQuery.equalTo("objectId", userId);
          const user = await userQuery.first({ useMasterKey: true });

          if (user) {
            const currentBalance = user.get("balance") || 0;
            user.set("balance", currentBalance + amount);
            await user.save(null, { useMasterKey: true });
          } else {
            console.warn(`⚠️ User not found for transaction ${txn.id}`);
          }
        }
      }
    } catch (err) {
      console.error("❌ Failed to update transaction:", err);
    }
  };

  // ✅ Handle postMessage from iframe
  const handleMessage = useCallback(
    (event) => {
      if (!allowedOrigins.includes(event.origin)) return;
      const message =
        typeof event.data === "string" ? JSON.parse(event.data) : event.data;

      switch (message?.type) {
        case "CHECKOUT_READY":
          setLoading(false);
          break;

        case "PAYMENT_SUCCESS":
          setPaymentStatus({ status: "success", amount: message.data?.amount });
          setShowSuccessAnimation(true);
          updateTransactionStatus(message.data?.sessionId, 2);
          ///setTimeout(() => navigate("/playerDashboard"), 3000);
          break;

        case "PAYMENT_FAILED":
          setPaymentStatus({ status: "failed", reason: message.data?.reason });
          updateTransactionStatus(message.data?.sessionId, 10);
          break;

        case "HEIGHT_CHANGED":
          if (iframeRef.current && message.data?.height) {
            iframeRef.current.height = message.data.height;
          }
          break;

          case "PAYMENT_CANCELLED":
            setPaymentStatus({ status: "cancelled", reason: message.data?.reason });
            updateTransactionStatus(message.data?.sessionId, 10);
            break;
        case "ERROR":
          setError("Checkout error occurred. Please try again.");
          break;

        default:
          console.warn("⚠️ Unknown event:", message?.type);
      }
    },
    [navigate]
  );

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [handleMessage]);

  if (!open) return null;

  return (
    <Box className="container py-4">
      <Box display="flex" justifyContent="start" mb={2}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={onClose}
        >
          Back
        </Button>
      </Box>

      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
          <CircularProgress size={20} />
          <Typography variant="body2" ml={1}>
            Loading Checkout...
          </Typography>
        </Box>
      )}
      {paymentStatus?.status === "success" && (
        <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
          <Alert severity="success">
            Successfully recharged {paymentStatus.amount}! You can close this
            window.
          </Alert>
        </Box>
      )}

      {paymentStatus?.status === "failed" && (
        <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
          <Alert severity="error">
            Payment failed: {paymentStatus.reason || "Please try again."} &nbsp;
            You can safely close this window.
          </Alert>
        </Box>
      )}

{paymentStatus?.status === "cancelled" && (
        <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
          <Alert severity="error">
            Payment cancelled: {paymentStatus.reason || "Please try again."} &nbsp;
            You can safely close this window.
          </Alert>
        </Box>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {checkoutResponse?.publicUrl  && paymentStatus?.status != "success" && paymentStatus?.status != "failed" && paymentStatus?.status !=  "cancelled" && (
        <iframe
          ref={iframeRef}
          id="clkk-checkout"
          src={checkoutResponse.publicUrl}
          title="CLKK Checkout"
          width="100%"
          height="600"
          style={{
            border: "1px solid #ccc",
            borderRadius: "8px",
            display: loading ? "none" : "block",
          }}
          allow="payment"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
        />
      )}
    </Box>
  );
};

export default CLKKWidget;
